import React, { useMemo } from "react";
import {
  Flex,
  useTranslation,
  Thead,
  Tr,
  Th,
  DraggableTable,
  DraggableTbody,
  arrayMove,
  useToast,
  Modal,
  ModalBody,
  Text,
} from "@familyzone/component-library";

import { FilteringRule } from "./types";

import PolicyTableRow from "./PolicyTableRow";
import NoContentDisplay from "./NoContentDisplay";

interface FilteringTableProps {
  loading?: boolean;
  disabled?: boolean;
  disableDrag?: boolean;
  searchFilter?: boolean;
  rules: FilteringRule[];
  filteredRules: FilteringRule[];
  onSortRules: (rules: FilteringRule[]) => void;
  onTempSortRules?: (rules: FilteringRule[]) => void;
  onEditRule: (rule: FilteringRule) => void;
  onDeleteRule: (rule: FilteringRule) => void;
  onToggleRule: (rule: FilteringRule) => void;
  onCopyRule: (rule: FilteringRule) => void;
}

const FilteringTable: React.FC<FilteringTableProps> = ({
  loading = false,
  disabled = false,
  disableDrag = false,
  searchFilter = false,
  rules = [],
  filteredRules = [],
  onSortRules = () => "",
  onTempSortRules = () => "",
  onEditRule = () => "",
  onDeleteRule = () => "",
  onToggleRule = () => "",

  onCopyRule = () => "",
}) => {
  const { t } = useTranslation();
  const { warningToast } = useToast();

  const columns: string[] = disableDrag
    ? [t("Enable"), t("Action"), t("Policy Name"), t("Operations")]
    : ["", t("Enable"), t("Action"), t("Policy Name"), t("Operations")];

  const noContentTitle = t(searchFilter ? "Policy do not match your search" : "Create your first policy");
  const noContentText = t(
    searchFilter
      ? "Please adjust your filter/search for more results"
      : "Create your first content filtering policy by clicking on the “Create Policy” button"
  );
  const lockedWarningText = t("Locked filter policies have greater priority and must remain at the top");
  const [openConfirmDialog, setOpenConfirmDialog] = React.useState(false);
  const [pendingMove, setPendingMove] = React.useState<{ active: string | number; over: string | number } | null>(null);
  const [oldRules, setOldRules] = React.useState<FilteringRule[]>([]);

  const titleText = t("Confirm Move");

  const sortedRules: FilteringRule[] = useMemo(() => {
    const sticky = filteredRules.filter((r) => r.sticky);
    const nonSticky = filteredRules.filter((r) => !r.sticky);
    return [...sticky, ...nonSticky];
  }, [filteredRules]);

  if (filteredRules.length === 0 && !loading) {
    return <NoContentDisplay title={noContentTitle} text={noContentText} />;
  }

  const handleMoveRule = () => {
    if (loading) return;
    if (!pendingMove) return;
    const { active, over } = pendingMove;
    if (active === over) return;

    onSortRules(rules);
    setPendingMove(null);
    setOpenConfirmDialog(false);
  };

  const handleOpenConfirmChange = (active: string | number, over: string | number) => {
    const activeRule = rules.find((r) => r.id === active);
    const overRule = rules.find((r) => r.id === over);
    if (
      (overRule && overRule.sticky && activeRule && !activeRule.sticky) ||
      (overRule && !overRule.sticky && activeRule && activeRule.sticky)
    ) {
      warningToast({ title: lockedWarningText, position: "bottom", duration: 6000 });
      setPendingMove(null);
      setOpenConfirmDialog(false);
      return;
    }
    const activeIndex = rules.findIndex((r) => r.id === active);
    const overIndex = rules.findIndex((r) => r.id === over);
    if (activeIndex === -1 || overIndex === -1) return;

    const newRules = arrayMove(rules, activeIndex, overIndex);

    setOldRules(rules);
    onTempSortRules(newRules);
    setPendingMove({ active, over });
    setOpenConfirmDialog(true);
  };

  const handleCancelChange = () => {
    onTempSortRules(oldRules);
    setPendingMove(null);
    setOpenConfirmDialog(false);
  };

  return (
    <>
      <Flex>
        <DraggableTable onMove={handleOpenConfirmChange}>
          <Thead>
            <Tr role="row">
              {columns.map((column, index) => (
                <Th
                  key={`${index}-${column}`}
                  headerText={column}
                  columnName={column}
                  alignment={index === columns.length - 1 ? "right" : "left"}
                  role="rowheader"
                />
              ))}
            </Tr>
          </Thead>
          <DraggableTbody items={sortedRules.map((r) => r.id)} loaded={!loading}>
            {sortedRules.map((rule) => (
              <PolicyTableRow
                draggable={!disableDrag}
                key={rule.id}
                rule={rule}
                id={rule.id}
                onToggleEnabled={() => onToggleRule(rule)}
                onEdit={() => onEditRule(rule)}
                onDelete={() => onDeleteRule(rule)}
                onCopy={() => onCopyRule(rule)}
                disabled={disabled}
              />
            ))}
          </DraggableTbody>
        </DraggableTable>
      </Flex>
      <Modal
        isOpen={openConfirmDialog}
        size="sm"
        onClose={handleCancelChange}
        headerText={titleText}
        primaryCTALabel={titleText}
        onPrimaryCTAClick={handleMoveRule}
        secondaryCTALabel="Cancel"
        onSecondaryCTAClick={handleCancelChange}
      >
        <ModalBody>
          <Text>{t(`Changing the order of Filtering Policies changes their priority.`)} </Text>
          <Text mt="16px">
            {t(`Do you want to move `)}
            <Text as="span" fontWeight="bold">
              {pendingMove?.active ? String(rules.find((rule) => rule.id === pendingMove?.active)?.name) : ""}
            </Text>
            {rules.findIndex((r) => r.id === pendingMove?.active) > oldRules.findIndex((r) => r.id === pendingMove?.active)
              ? t(" below ")
              : t(" above ")}
            <Text as="span" fontWeight="bold">
              {pendingMove?.over ? String(rules.find((rule) => rule.id === pendingMove?.over)?.name) : ""}
              {t("?")}
            </Text>
          </Text>
        </ModalBody>
      </Modal>
    </>
  );
};

export default FilteringTable;
